import {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react';
import {FieldError, useFormContext} from 'react-hook-form';
import {
    addMonths,
    endOfDay,
    getDaysInMonth,
    isAfter,
    isBefore,
    isSameMonth,
    lastDayOfMonth,
    setDate,
    startOfDay,
    subMonths,
} from 'date-fns';
import {fromZonedTime, toZonedTime} from 'date-fns-tz';
import useOutsideDetector from 'hooks/useOutsideDetector';
import {JST_TIMEZONE} from 'utils/date';
import {
    getDefaultDate,
    getElementById,
    getIdFromDate,
    getSafeDate,
    isWithinRange,
    MinuteStep,
} from '../utils';
import useKeyDown from './useKeyDown';

type DateValue = Date | undefined;

export interface UseDateSelectProps {
    disabled?: boolean;
    error?: FieldError;
    maxDate?: Date;
    minDate?: Date;
    minuteStep?: MinuteStep;
    name: string;
    required?: boolean;
    showTime?: boolean;
}

const useDateSelect = ({
    disabled,
    error,
    maxDate,
    minDate,
    minuteStep,
    name,
    required,
    showTime,
}: UseDateSelectProps) => {
    // Id
    const id = useId();

    // Element
    const refInput = useRef<HTMLInputElement>(null);
    const refPortal = useRef<HTMLDivElement>(null);

    // Show-hide panels
    const [show, setShow] = useState(false);

    const onShow = useCallback(() => {
        setShow(true);
        if (!show) refPortal.current?.focus();
    }, [show]);
    const onHide = useCallback(() => setShow(false), []);

    useOutsideDetector([refInput, refPortal], () => onHide());

    // Form
    const {setValue, watch} = useFormContext<Record<string, DateValue>>();
    const value = watch(name);

    // Date Panel
    const [visibleDate, setVisibleDate] = useState(
        value || getDefaultDate(minDate, maxDate),
    );

    // Current Focus
    const [currentFocus, setCurrentFocus] = useState<string | undefined>(
        undefined,
    );

    // Values Zoned at JST
    const valueZonedAtJst = useMemo(
        () => (value ? toZonedTime(value, JST_TIMEZONE) : undefined),
        [value],
    );

    const visibleDateZonedAtJst = useMemo(
        () => toZonedTime(visibleDate, JST_TIMEZONE),
        [visibleDate],
    );

    const setVisibleDateZonedAtJst = useCallback((dateZonedAtJst: Date) => {
        const newVisibleDate = fromZonedTime(dateZonedAtJst, JST_TIMEZONE);
        setVisibleDate(newVisibleDate);
    }, []);

    useEffect(() => {
        if (currentFocus && refInput.current !== document.activeElement) {
            const element = getElementById(currentFocus);
            if (element && !element.disabled) element.focus();
        }
    }, [currentFocus]);

    useEffect(() => {
        if (show && value === undefined && currentFocus === undefined) {
            const date = getDefaultDate(minDate, maxDate);
            setCurrentFocus(getIdFromDate(id, date) || undefined);
        } else if (!show) {
            setCurrentFocus(undefined);
        }
    }, [currentFocus, id, maxDate, minDate, show, value]);

    const minimumDate = useMemo(
        () => (minDate ? startOfDay(minDate) : undefined),
        [minDate],
    );
    const maximumDate = useMemo(
        () => (maxDate ? endOfDay(maxDate) : undefined),
        [maxDate],
    );

    const isNextDisabled = useMemo(
        () => !!maxDate && isSameMonth(maxDate, visibleDate),
        [maxDate, visibleDate],
    );

    const isPreviousDisabled = useMemo(
        () => !!minDate && isSameMonth(minDate, visibleDate),
        [minDate, visibleDate],
    );

    const onChangeZonedAtJst = useCallback(
        (dateZonedAtJst: DateValue, keepPanelOpen = false) => {
            const date = dateZonedAtJst
                ? fromZonedTime(dateZonedAtJst, JST_TIMEZONE)
                : undefined;
            setValue(name, date);

            if (!keepPanelOpen && !showTime) onHide();

            if (dateZonedAtJst) {
                const day = Math.min(
                    getDaysInMonth(dateZonedAtJst),
                    visibleDate.getDate(),
                );

                setVisibleDateZonedAtJst(setDate(dateZonedAtJst, day));
                setCurrentFocus(undefined);
            }
        },
        [
            name,
            onHide,
            setValue,
            setVisibleDateZonedAtJst,
            showTime,
            visibleDate,
        ],
    );

    const isValid = useMemo(
        () => isWithinRange(value, minimumDate, maximumDate),
        [maximumDate, minimumDate, value],
    );

    useEffect(() => {
        if (!isValid && value) {
            const validDate =
                !minimumDate || isAfter(value, minimumDate)
                    ? !maximumDate || isBefore(value, maximumDate)
                        ? value
                        : maximumDate
                    : minimumDate;

            const validDateZonedAtJst = toZonedTime(validDate, JST_TIMEZONE);

            onChangeZonedAtJst(getSafeDate(validDateZonedAtJst));
        }
    }, [isValid, maximumDate, minimumDate, onChangeZonedAtJst, value]);

    const onNext = useCallback(() => {
        if (!isNextDisabled) {
            const nextMonthInJst = addMonths(visibleDateZonedAtJst, 1);
            const nextMonth = fromZonedTime(nextMonthInJst, JST_TIMEZONE);

            if (!maxDate || isBefore(nextMonth, maxDate)) {
                const day = Math.min(
                    getDaysInMonth(nextMonthInJst),
                    visibleDateZonedAtJst.getDate(),
                );

                setVisibleDateZonedAtJst(setDate(nextMonthInJst, day));
            } else {
                setVisibleDate(maxDate);
            }
        }
    }, [
        isNextDisabled,
        maxDate,
        setVisibleDateZonedAtJst,
        visibleDateZonedAtJst,
    ]);

    const onPrevious = useCallback(() => {
        if (!isPreviousDisabled) {
            const previousMonthZonedAtJST = lastDayOfMonth(
                subMonths(
                    new Date(
                        visibleDateZonedAtJst.getFullYear(),
                        visibleDateZonedAtJst.getMonth(),
                    ),
                    1,
                ),
            );

            const previousMonth = fromZonedTime(
                previousMonthZonedAtJST,
                JST_TIMEZONE,
            );

            if (!minDate || isAfter(previousMonth, minDate)) {
                const day = Math.min(
                    getDaysInMonth(previousMonthZonedAtJST),
                    visibleDateZonedAtJst.getDate(),
                );

                setVisibleDateZonedAtJst(setDate(previousMonthZonedAtJST, day));
            } else {
                setVisibleDate(minDate);
            }
        }
    }, [
        isPreviousDisabled,
        minDate,
        setVisibleDateZonedAtJst,
        visibleDateZonedAtJst,
    ]);

    useKeyDown({
        currentFocus,
        id,
        isNextDisabled,
        isPreviousDisabled,
        onNext,
        onPrevious,
        onShow,
        refInput,
        setCurrentFocus,
    });

    return {
        currentFocus,
        disabled,
        error,
        id,
        isNextDisabled,
        isPreviousDisabled,
        maxDate: maximumDate,
        minDate: minimumDate,
        minuteStep,
        onChangeZonedAtJst,
        onHide,
        onNext,
        onPrevious,
        onShow,
        refInput,
        refPortal,
        required,
        setCurrentFocus,
        show,
        showTime,
        valueZonedAtJst,
        visibleDateZonedAtJst,
    };
};

export default useDateSelect;
